import axios from 'axios';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import API_ENDPOINTS from '../config/api';

// Configure axios defaults
axios.defaults.baseURL = API_ENDPOINTS.BASE_URL;
// Reason: backend throttles messages-per-hour per (user × platform). Tag
// every request from the web app so the server picks the web limits from
// MessageRateLimitPolicy. Mobile sets this header to 'mobile' separately.
axios.defaults.headers.common['X-Client-Platform'] = 'web';

// Types
interface User {
  id: string;
  email: string;
  name: string;
  display_name?: string;
  subscription_provider_name?: string;
  user_submitted_interests?: string;
  phone?: string;
  tags: string[];
  voice_gender?: string;
  voice_language?: string;
  consent_required?: boolean;
  provider_terms_required?: boolean;
  active_membership?: {
    id: string;
    provider: {
      id: string;
      business_name: string;
    };
    site: {
      id: string;
      name: string;
      background_colour?: string;
      logo_url?: string | null;
      default_avatar?: {
        id: number;
        display_name: string;
        image_url: string;
      } | null;
    };
    joined_at: string;
    needs_terms_acceptance?: boolean;
    provider_terms_version?: string;
    provider_privacy_version?: string;
    terms_accepted_version?: string;
    privacy_accepted_version?: string;
  } | null;
}

export interface RegisterResult {
  verificationRequired: boolean;
  email: string;
  user: User | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  register: (
    email: string,
    password: string,
    termsAccepted: boolean,
    aiConsent: boolean,
    name?: string,
    signupInviteSecret?: string,
  ) => Promise<RegisterResult>;
  deleteAccount: () => Promise<void>;
  refreshUser: () => Promise<void>;
  syncUser: (user: User) => void;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track refresh attempts to prevent infinite loops
  const refreshAttempts = React.useRef(0);
  const isRefreshing = React.useRef(false);
  const authCleared = React.useRef(false); // Flag to prevent refresh after auth is cleared
  const MAX_REFRESH_ATTEMPTS = 3;

  // Clear authentication state
  const clearAuth = React.useCallback(() => {
    console.log('[Auth] Clearing authentication state');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    refreshAttempts.current = 0;
    isRefreshing.current = false;
    authCleared.current = true; // Set flag to prevent further refresh attempts

    // Redirect to login page
    if (
      window.location.pathname !== '/login' &&
      window.location.pathname !== '/register' &&
      window.location.pathname !== '/verify-email'
    ) {
      console.log('[Auth] Redirecting to login page');
      window.location.href = '/login';
    }
  }, []);

  // Token refresh function with retry protection
  const refreshToken = React.useCallback(async (): Promise<boolean> => {
    // Don't attempt refresh if auth has already been cleared
    if (authCleared.current) {
      console.log('[Auth] Auth already cleared, skipping refresh attempt');
      return false;
    }

    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing.current) {
      console.log('[Auth] Refresh already in progress, skipping');
      return false;
    }

    // Check if we've exceeded max attempts
    if (refreshAttempts.current >= MAX_REFRESH_ATTEMPTS) {
      console.warn('[Auth] Max token refresh attempts exceeded, clearing auth');
      clearAuth();
      return false;
    }

    try {
      isRefreshing.current = true;
      refreshAttempts.current += 1;

      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        console.warn('[Auth] No refresh token available');
        clearAuth();
        return false;
      }

      console.log(`[Auth] Token refresh attempt ${refreshAttempts.current}/${MAX_REFRESH_ATTEMPTS}`);

      const response = await axios.post(API_ENDPOINTS.AUTH.REFRESH, {
        refresh: refreshTokenValue
      });

      const newAccessToken = response.data.access;
      localStorage.setItem('access_token', newAccessToken);
      setToken(newAccessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

      // Handle token rotation: store new refresh token if provided
      if (response.data.refresh) {
        localStorage.setItem('refresh_token', response.data.refresh);
        console.log('[Auth] New refresh token stored due to token rotation');
      }

      // Reset attempt counter and authCleared flag on successful refresh
      refreshAttempts.current = 0;
      authCleared.current = false;

      console.log('[Auth] Token refresh successful');
      return true;
    } catch (error: any) {
      console.error(`[Auth] Token refresh failed (attempt ${refreshAttempts.current}):`, error?.response?.data || error.message);

      // If refresh token is invalid/expired, clear auth immediately
      if (error?.response?.status === 401) {
        console.warn('[Auth] Refresh token invalid/expired, clearing auth');
        clearAuth();
        return false;
      }

      // For other errors, allow retry up to max attempts
      return false;
    } finally {
      isRefreshing.current = false;
    }
  }, [clearAuth]);

  // Set up axios interceptor for automatic token refresh
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only handle 401 errors for authentication, ignore 500/503 server errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Skip refresh for auth endpoints to prevent infinite loops
          if (originalRequest.url?.includes('/auth/token/refresh/') ||
            originalRequest.url?.includes('/auth/login/') ||
            originalRequest.url?.includes('/auth/register/')) {
            console.warn('[Auth] Auth endpoint failed, not attempting refresh');
            return Promise.reject(error);
          }

          // Don't attempt refresh if auth has been cleared
          if (authCleared.current) {
            console.log('[Auth] Auth already cleared, rejecting request');
            return Promise.reject(error);
          }

          console.log('[Auth] 401 error detected, attempting token refresh');
          const refreshed = await refreshToken();
          if (refreshed) {
            // Update the original request with new token
            const newToken = localStorage.getItem('access_token');
            if (newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            console.log('[Auth] Retrying original request with new token');
            // Retry the original request with new token
            return axios(originalRequest);
          } else {
            // Refresh failed, auth was already cleared by refreshToken function
            console.warn('[Auth] Token refresh failed, user will need to login again');
          }
        }

        // For 5xx server errors, don't clear authentication - just let the error propagate
        // This prevents users from being logged out due to temporary server issues
        if (error.response?.status >= 500) {
          console.warn('[Auth] Server error detected, maintaining authentication state:', error.response.status);
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshToken]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('access_token');
        if (storedToken) {
          setToken(storedToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Verify token and get user info
          try {
            const response = await axios.get(API_ENDPOINTS.AUTH.USER_INFO);
            setUser(response.data.user);
          } catch (error: any) {
            // If token is invalid, try to refresh it once during initialization
            if (error.response?.status === 401) {
              const refreshed = await refreshToken();
              if (refreshed) {
                // Try again with new token
                try {
                  const response = await axios.get(API_ENDPOINTS.AUTH.USER_INFO);
                  setUser(response.data.user);
                } catch (retryError) {
                  throw retryError;
                }
              } else {
                throw error; // Refresh failed, clear tokens
              }
            } else {
              throw error;
            }
          }
        }
      } catch (error: any) {
        // Clear invalid tokens using the centralized function
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [refreshToken, clearAuth]);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
      });

      const { tokens, user: userData } = response.data;
      const { access, refresh } = tokens;

      // Store tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      // Update state
      setToken(access);
      setUser(userData);

      // Reset flags on successful login
      refreshAttempts.current = 0;
      authCleared.current = false;

      return userData;
    } catch (error: any) {
      // Reason: DRF returns non_field_errors for bad credentials, not detail
      const data = error.response?.data;
      let errorMessage = 'Login failed';
      if (error.response?.status === 400) {
        errorMessage = 'Your email and/or password have not been recognised';
      } else if (data?.detail) {
        errorMessage = data.detail;
      } else if (data?.non_field_errors?.[0]) {
        errorMessage = data.non_field_errors[0];
      }
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    termsAccepted: boolean,
    aiConsent: boolean,
    name?: string,
    signupInviteSecret?: string,
  ): Promise<RegisterResult> => {
    try {
      setError(null);
      setLoading(true);

      // Reason: when the user arrived at /register via /join/<secret>
      // (handled by JoinPage routing for unauthenticated visitors),
      // pipe the secret through so the backend can stamp
      // `User.signed_up_via_invite_token` for Provider 'Lives
      // Improved' attribution. Pure attribution — the redeem itself
      // still happens later via the existing Profile flow.
      const response = await axios.post(API_ENDPOINTS.AUTH.REGISTER, {
        email,
        name: name ?? '',
        password,
        password_confirm: password,
        terms_accepted: termsAccepted,
        ai_consent: aiConsent,
        ...(signupInviteSecret ? { signup_invite_secret: signupInviteSecret } : {}),
      });

      // Reason: open-signup returns 202 with no tokens — the user must
      // confirm their email before they're signed in. Bubble that up so
      // RegisterPage can show a 'check your email' state.
      if (response.data?.verification_required) {
        return {
          verificationRequired: true,
          email: response.data.email ?? email,
          user: null,
        };
      }

      const { tokens, user: userData } = response.data;
      const { access, refresh } = tokens;

      // Store tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      // Update state
      setToken(access);
      setUser(userData);

      // Reset flags on successful registration
      refreshAttempts.current = 0;
      authCleared.current = false;

      return { verificationRequired: false, email: userData.email, user: userData };
    } catch (error: any) {
      // Reason: provide a subscription-aware message for unrecognised signups
      const SIGNUP_ERROR =
        'Your details have not been recognised \u2014 please try again if you ' +
        'know you have been provided a Jeff Says subscription.\n\n' +
        'Jeff Says is a subscription-only service right now \u2014 please ' +
        'refer to your employer if you believe they have subscribed for you, ' +
        'or go to happihubuk.com for more information about subscription plans.';

      let errorMessage = SIGNUP_ERROR;

      if (error.response?.data) {
        const data = error.response.data;

        // Handle specific DRF field validation errors (e.g. password too short)
        if (data.password && Array.isArray(data.password)) {
          errorMessage = data.password[0];
        } else if (data.password_confirm && Array.isArray(data.password_confirm)) {
          errorMessage = data.password_confirm[0];
        }
        // For email errors and non_field_errors, use the subscription message
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = React.useCallback(async (): Promise<void> => {
    try {
      const response = await axios.get(API_ENDPOINTS.AUTH.USER_INFO);
      setUser(response.data.user);
    } catch (err) {
      console.error('[Auth] Failed to refresh user:', err);
    }
  }, []);

  const syncUser = React.useCallback((nextUser: User): void => {
    setUser(nextUser);
  }, []);

  const deleteAccount = async (): Promise<void> => {
    await axios.post(API_ENDPOINTS.AUTH.DELETE_ACCOUNT);
    clearAuth();
    setError(null);
  };

  const logout = React.useCallback(() => {
    // Clear all auth state
    clearAuth();
    setError(null);

    // Optional: Call logout endpoint
    axios.post(API_ENDPOINTS.AUTH.LOGOUT).catch(console.error);

    console.log('User logged out successfully');
  }, [clearAuth]);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    deleteAccount,
    refreshUser,
    syncUser,
    logout,
    loading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
